import { LazyLoadImages } from '../../lazy-loading/LazyLoadImages'
import { getDiscountProductPrice } from '../../api/variants'

// Widest grid breakpoint renders 4 columns per row (see _filter-results.scss render-col-4) —
// these are above-the-fold on first paint, so skip the lazy-load hop for them.
const PRIORITY_IMAGE_COUNT = 4

export default class FilterResults {
    constructor(hideBulletPoints) {
        this.page = 1
        this.isLoading = true
        this.hideBulletPoints = hideBulletPoints
        this.productListing = document.getElementById('product_listing')
        this.productCountContainer = document.getElementById('product_listing_count')
        this.paginationContainer = document.getElementById('pagination')
        this.hostOrigin = window.location.origin
        this.totalPages = 1
        history.scrollRestoration = 'manual'
    }

    generateFeature(feature) {
        const featureWrapper = document.createElement('li')
        let featureKey = this.cutBR(feature.key)
        const featureValue = feature.value
        const featureUnit = feature.unit
        const featureType = typeof featureValue
        let transformedValue = ''
        if (featureType === 'object') {
            for (let [key, value] of Object.entries(featureValue)) {
                if (key === 'min') {
                    transformedValue += `${value} - `
                } else if (key === 'max') {
                    transformedValue += `${value}`
                } else {
                    transformedValue += `${value}, `
                }
            }
        } else {
            transformedValue = String(featureValue)
        }
        transformedValue = transformedValue.replace(/(,\s?)$/g, '')
        if (typeof featureUnit === 'string' && featureUnit.length > 0) {
            transformedValue += ` ${featureUnit}`
        }
        featureKey = this.cutBR(featureKey)
        transformedValue = this.cutBR(transformedValue)

        featureWrapper.setAttribute('title', `${featureKey}: ${transformedValue}`)
        featureWrapper.innerHTML = `${featureKey}: ${transformedValue}`

        return featureWrapper
    }

    generateProductImage(product, isPriority = false) {
        const productImageWrapper = document.createElement('div')
        productImageWrapper.classList.add('item-image')
        if (product.rental == true) {
            const rentalImageTag = document.createElement('span')
            rentalImageTag.classList.add('rental-image-tag')
            const rentalImage = document.createElement('img')
            rentalImage.setAttribute('data-src', this.productListing.getAttribute('data-rentalIcon'))
            rentalImage.setAttribute('alt', this.productListing.getAttribute('data-rentalIconAltText'));
            rentalImageTag.appendChild(rentalImage)
            productImageWrapper.appendChild(rentalImageTag)
        }
        const productImage = document.createElement('img')
        const azureThumbnailUrl = `${window.globals.setting('frontendSourceThumbnail')}`;
        var imageURL = azureThumbnailUrl + product.image;

        productImage.classList.add('image')
        if (isPriority) {
            // First row renders above the fold: skip the IntersectionObserver hop and
            // signal the browser to fetch it immediately, so it can compete for LCP.
            productImage.setAttribute('src', imageURL)
            productImage.setAttribute('loading', 'eager')
            productImage.setAttribute('fetchpriority', 'high')
        } else {
            productImage.classList.add('lazy')
            productImage.setAttribute('data-src', imageURL)
        }
        productImage.setAttribute('alt', product.imageAlt)
        productImage.setAttribute('width', '300')
        productImage.setAttribute('height', '300')
        productImageWrapper.appendChild(productImage)
        return productImageWrapper
    }

    generateProductDescription(product) {
        const text = product.listingText
        const attributes = product.listingAttributes
        const name = product.name
        const hasProductText = text !== ''
        const hasAttributes = attributes.length > 0

        const productDescWrapper = document.createElement('div')
        productDescWrapper.classList.add('item-desc')
        const productName = document.createElement('h2')
        productName.classList.add('item-name')
        productName.innerHTML = name
        productDescWrapper.appendChild(productName)

        if (hasProductText || hasAttributes) {
            const scrollWrapper = document.createElement('div')
            scrollWrapper.classList.add('scrollable-group')
            if (hasProductText) {
                const textElem = document.createElement('div')
                textElem.classList.add('item-text')
                textElem.innerHTML = text
                scrollWrapper.appendChild(textElem)
            }
            if (hasAttributes) {
                const listElem = document.createElement('ul')
                listElem.classList.add('item-features')
                if (!this.hideBulletPoints) {
                    listElem.classList.add('with-bullet')
                }
                attributes.map((attr) => {
                    listElem.appendChild(this.generateFeature(attr))
                })
                scrollWrapper.appendChild(listElem)
            }
            productDescWrapper.appendChild(scrollWrapper)
        } else {
            const emptyElem = document.createElement('div')
            emptyElem.classList.add('item-empty')
            productDescWrapper.appendChild(emptyElem)
        }

        return productDescWrapper
    }

    generateProductButton(product) {
        const productButtonWrapper = document.createElement('div')
        productButtonWrapper.classList.add('item-button')
        const productButton = document.createElement('a')
        productButton.classList.add('button')
        productButton.href = this.generateProductUrl(product.url)
        productButton.innerHTML = `${window.globals.trans('filter.view_product')}`
        productButtonWrapper.appendChild(productButton)
        return productButtonWrapper
    }

    generateProductUrl(url) {
        if (!url) return ''
        const params = window.location.href.split('#')[1] || ''
        return `${url}#${params}`
    }

    generateProduct(product, isPriority = false) {
        const pWrapper = document.createElement('a')
        pWrapper.href = this.generateProductUrl(product.url)
        pWrapper.classList.add('product-wrapper')
        pWrapper.setAttribute('data-product-id', product.id)
        pWrapper.addEventListener('click', () => {
            sessionStorage.setItem('plp_scroll', JSON.stringify({
                productId: product.id,
                viewportOffset: Math.round(pWrapper.getBoundingClientRect().top),
                url: window.location.pathname + window.location.search
            }))
            if (product.categoryId) {
                sessionStorage.setItem(`productCategory_${product.id}`, product.categoryId)
                document.cookie = `lastProductCategory=${product.id}:${product.categoryId}; path=/; max-age=300`
            }
        })
        const pUrl = document.createElement('div')
        pUrl.classList.add('item')
        const pImage = this.generateProductImage(product, isPriority)
        const pDescription = this.generateProductDescription(product)
        const pPriceButtonWrapper = this.generatePriceButtonWrapper()
        const pPrice = this.generateProductPrice(product)
        const pButton = this.generateProductButton(product)

        pUrl.appendChild(pImage)
        pUrl.appendChild(pDescription)
        pUrl.appendChild(pPriceButtonWrapper)
        pPriceButtonWrapper.appendChild(pPrice)
        pPriceButtonWrapper.appendChild(pButton)
        pWrapper.appendChild(pUrl)

        return pWrapper
    }

    generatePriceButtonWrapper() {
        const wrapper = document.createElement('div')
        wrapper.classList.add('item-price-button-wrapper')
        return wrapper
    }

    generateProductPrice(product) {
        const priceElement = this.createPriceElement()

        if (product.minPriceVariantId) {
            this.fetchAndDisplayDiscountPrice(product, priceElement)
        } else {
            this.displayMinPrice(product, priceElement)
        }

        return priceElement
    }

    createPriceElement() {
        const priceElement = document.createElement('div')
        priceElement.classList.add('item-price')
        return priceElement
    }

    fetchAndDisplayDiscountPrice(product, priceElement) {
        getDiscountProductPrice(product.minPriceVariantId)
            .then((res) => {
                const discountPrice = res.data
                const innerHTMLContent = this.generateInnerHTMLContent(product, discountPrice)
                priceElement.innerHTML = innerHTMLContent
            })
            .catch((error) => {
                console.error('Error fetching discount price:', error)
                priceElement.innerHTML = '&nbsp;'
            })
    }

    generateInnerHTMLContent(product, discountPrice) {
        if (product.minPrice && product.minPrice !== '' && product.minPrice !== discountPrice.PriceFormatted) {
            return `<span>${window.globals.trans('filter.min_price')}</span>
                <span style="text-decoration: line-through; opacity: 0.5;">${product.minPrice}</span>
                <span class="discount-price">${discountPrice.PriceFormatted}</span>`
        } else {
            return `<span>${window.globals.trans('filter.min_price')}</span> ${product.minPrice}`
        }
    }

    displayMinPrice(product, priceElement) {
        if (product.minPrice && product.minPrice !== '') {
            priceElement.innerHTML = `<span>${window.globals.trans('filter.min_price')}</span> ${product.minPrice}`
        } else {
            priceElement.innerHTML = '&nbsp;'
        }
    }

    generateProductsList(productsData) {
        if (productsData.length > 0) {
            productsData.map((product, index) => {
                this.productListing.appendChild(this.generateProduct(product, index < PRIORITY_IMAGE_COUNT))
            })
            new LazyLoadImages(this.productListing)
        } else {
            const noResults = document.createElement('div')
            noResults.classList.add('col-12')
            noResults.classList.add('text-center')
            noResults.innerHTML = `${window.globals.trans('filter.no_results')}`
            this.productListing.appendChild(noResults)
        }
    }

    generateTotalCount(totalCount) {
        this.productCountContainer.innerHTML = totalCount
    }

    render(productsData, totalCount, perPage, initialPage = 1) {
        this.page = initialPage
        this.totalPages = Math.ceil(totalCount / perPage) || 1
        this.productListing.innerHTML = ''
        this.generateTotalCount(totalCount)
        this.generateProductsList(productsData)
        this.generatePagination(this.page, this.totalPages)
        this.paginationContainer.style.display = ''
        this.restoreScrollPosition()
    }

    restoreScrollPosition() {
        const saved = sessionStorage.getItem('plp_scroll')
        if (!saved) return
        try {
            const { productId, viewportOffset, url } = JSON.parse(saved)
            if (url !== window.location.pathname + window.location.search) return
            sessionStorage.removeItem('plp_scroll')
            requestAnimationFrame(() => {
                const el = this.productListing.querySelector(`[data-product-id="${productId}"]`)
                if (!el) return
                const elTop = el.getBoundingClientRect().top + window.scrollY
                window.scrollTo({ top: Math.max(0, elTop - viewportOffset), behavior: 'instant' })
            })
        } catch (e) {}
    }

    getPageRange(currentPage, totalPages) {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }
        if (currentPage <= 4) {
            return [1, 2, 3, 4, 5, '...', totalPages]
        }
        if (currentPage >= totalPages - 3) {
            return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
        }
        return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
    }

    generatePagination(currentPage, totalPages) {
        if (!this.paginationContainer) return
        this.paginationContainer.innerHTML = ''
        if (totalPages <= 1) return

        const nav = document.createElement('nav')
        nav.setAttribute('aria-label', 'Product pagination')

        const ul = document.createElement('ul')
        ul.classList.add('pagination')

        // Previous link
        const prevLi = document.createElement('li')
        prevLi.classList.add('page-item')
        if (currentPage === 1) {
            prevLi.classList.add('disabled')
            const prevSpan = document.createElement('span')
            prevSpan.classList.add('page-link', 'page-prev')
            prevSpan.setAttribute('aria-hidden', 'true')
            prevSpan.innerHTML = '&lt;'
            prevLi.appendChild(prevSpan)
        } else {
            const prevA = document.createElement('a')
            prevA.classList.add('page-link', 'page-prev')
            prevA.setAttribute('aria-label', 'Previous')
            prevA.setAttribute('href', `${window.location.pathname}?page=${currentPage - 1}`)
            prevA.innerHTML = '&lt;'
            prevA.addEventListener('click', (e) => { e.preventDefault(); this.goToPage(currentPage - 1) })
            prevLi.appendChild(prevA)
        }
        ul.appendChild(prevLi)

        // Page number links
        this.getPageRange(currentPage, totalPages).forEach((item) => {
            const li = document.createElement('li')
            li.classList.add('page-item')
            if (item === '...') {
                li.classList.add('disabled')
                const span = document.createElement('span')
                span.classList.add('page-link')
                span.innerHTML = '&hellip;'
                li.appendChild(span)
            } else if (item === currentPage) {
                li.classList.add('active')
                const a = document.createElement('a')
                a.classList.add('page-link')
                a.setAttribute('aria-current', 'page')
                a.setAttribute('href', `${window.location.pathname}?page=${item}`)
                a.textContent = item
                li.appendChild(a)
            } else {
                const a = document.createElement('a')
                a.classList.add('page-link')
                a.setAttribute('href', `${window.location.pathname}?page=${item}`)
                a.textContent = item
                a.addEventListener('click', (e) => { e.preventDefault(); this.goToPage(item) })
                li.appendChild(a)
            }
            ul.appendChild(li)
        })

        // Next link
        const nextLi = document.createElement('li')
        nextLi.classList.add('page-item')
        if (currentPage === totalPages) {
            nextLi.classList.add('disabled')
            const nextSpan = document.createElement('span')
            nextSpan.classList.add('page-link', 'page-next')
            nextSpan.setAttribute('aria-hidden', 'true')
            nextSpan.innerHTML = '&gt;'
            nextLi.appendChild(nextSpan)
        } else {
            const nextA = document.createElement('a')
            nextA.classList.add('page-link', 'page-next')
            nextA.setAttribute('aria-label', 'Next')
            nextA.setAttribute('href', `${window.location.pathname}?page=${currentPage + 1}`)
            nextA.innerHTML = '&gt;'
            nextA.addEventListener('click', (e) => { e.preventDefault(); this.goToPage(currentPage + 1) })
            nextLi.appendChild(nextA)
        }
        ul.appendChild(nextLi)

        nav.appendChild(ul)
        this.paginationContainer.appendChild(nav)
    }

    goToPage(pageNo) {
        if (pageNo < 1 || pageNo > this.totalPages || pageNo === this.page) return
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        hashParams.delete('page')
        const hashStr = hashParams.toString() ? '#' + hashParams.toString() : ''
        const pageStr = pageNo > 1 ? `?page=${pageNo}` : ''
        window.location.href = `${window.location.pathname}${pageStr}${hashStr}`
    }

    scrollToListing() {
        const listing = document.querySelector('.product-listing')
        if (listing) {
            const offset = listing.getBoundingClientRect().top + window.pageYOffset - 100
            window.scrollTo({ top: offset, behavior: 'smooth' })
        }
    }

    cutBR(text) {
        return text.toString().replace(/(<|&lt;)br\s*\/*(>|&gt;)/g, ' ')
    }
}
